import { Component, ElementRef, EventEmitter, HostListener, Inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import * as XLSX from 'xlsx';
import { CommonModule } from '@angular/common';
import { Pair } from '../interfaces/pair';
import { Recipient } from '../interfaces/recipient';
import { ParticipantService } from '../services/participant.service';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { BehaviorSubject, Subject } from 'rxjs';
import { MatDialog, MatDialogModule, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CenterService } from '../services/center.service';
import { Center } from '../interfaces/center';
import { Donor } from '../interfaces/donor';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ClipboardModule } from 'ngx-clipboard';
import { NotifyService } from '../common/notify/notify.service';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatDateFormats } from '@angular/material/core';
import { MatMomentDateModule, MomentDateAdapter } from '@angular/material-moment-adapter';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormsModule } from '@angular/forms';
import Papa from 'papaparse';
import { MatButtonModule } from '@angular/material/button';
import { EnisSyncService, EnisSyncParticipantLog } from '../services/enis-sync.service';

export const MY_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: 'DD/MM/YYYY'
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};

function stripQuotes(s: string): string {
  let start = 0;
  let end = s.length;
  while (start < end && s[start] === '"') start++;
  while (end > start && s[end - 1] === '"') end--;
  return s.slice(start, end).trim();
}

@Component({
  selector: 'app-enis-import',
  templateUrl: './enis-import.component.html',
  styleUrls: ['./enis-import.component.scss'],
  providers: [NotifyService, { provide: DateAdapter, useClass: MomentDateAdapter }, { provide: MAT_DATE_LOCALE, useValue: 'en-GB' }, { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS }],
  imports: [
    CommonModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatDialogModule,
    MatFormFieldModule,
    ClipboardModule,
    MatMomentDateModule,
    MatCheckboxModule,
    FormsModule,
    MatButtonModule
  ]
})
export class EnisImportComponent implements OnInit {
  excelDataRecipients!: Recipient[];
  excelDataDonors!: Donor[];
  responseSubjectNewRecipientList: Subject<Recipient[]> = new Subject<Recipient[]>();
  responseSubjectNewDonorList: Subject<Donor[]> = new Subject<Donor[]>();
  /** set to true to not display the (large) icon */
  @Input() compact = false;

  /** emits once a file is succesfully processed with the number of patients imported */
  @Output() processed = new EventEmitter<number>();

  @ViewChild('fileInput', { static: true }) private readonly fileInput!: ElementRef;
  @ViewChild('input', { static: true }) input!: ElementRef;

  /** boolean toggle to indicate if user is hovering over component with a file to drop */
  hover$ = new BehaviorSubject(false);
  /** (possibly empty) list of errors */
  errors$ = new BehaviorSubject<string[]>([]);
  busy$ = new BehaviorSubject(false);
  centers!: Center[];
  enisString: string = '';
  isPostmortemWaitlist: boolean = false;
  private currentFileName?: string;
  private importLog: EnisSyncParticipantLog[] = [];

  constructor(
    private readonly participantService: ParticipantService,
    private readonly snackBar: MatSnackBar,
    private readonly centerService: CenterService,
    private readonly notify: NotifyService,
    private readonly dialog: MatDialog,
    private readonly enisSyncService: EnisSyncService
  ) {}

  ngOnInit(): void {
    this.getAllCenters();
    this.loadParticipants();
  }

  drag(event: DragEvent): void {
    this.hover$.next(event.type === 'dragover');
  }

  drop(event: DragEvent): void {
    const files = (event.target as HTMLInputElement | null)?.files || event.dataTransfer?.files;
    this.hover$.next(false);
    if (!files) return;
    this.readDataFromExcel(files);
  }

  @HostListener('document:dragover')
  @HostListener('document:dragleave')
  @HostListener('document:drop')
  cancelEvent(): boolean {
    // prevent default drag-drop outside of our component
    return false;
  }

  private loadParticipants(): void {
    this.participantService.getAllParticipants().subscribe((participants) => {
      this.enisString = participants
        .filter((p) => {
          if (p.type === 'Recipient' && (p as Recipient).postmortumwaitlist) return false;
          return true;
        })
        .map((p: any) => {
          let prefix = '';
          if (p.type === 'Recipient') prefix = 'R';
          else if (p.type === 'Donor') prefix = 'D';
          return prefix + (p.enisNumber || '');
        })
        .join(';');
      console.log('Loaded participants:', participants);
      console.log('ENIS string:', this.enisString);
    });
  }

  onFileChange(event: Event): void {
    const input = event.currentTarget as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.readDataFromExcel(input.files);
    } else {
      console.log('No document found');
    }
  }

  readDataFromExcel(files: FileList): void {
    this.busy$.next(true);
    if (files && files.length > 0) {
      const file = files[0];
      this.currentFileName = file.name;
      this.importLog = [];
      file.text().then((text) => {
        const isCsv = file.name.toLowerCase().endsWith('.csv');
        let rows: any[] = [];
        if (isCsv) {
          const result = Papa.parse(text, { delimiter: '', skipEmptyLines: true });
          rows = result.data;
        } else {
          const workbook = XLSX.read(text, { type: 'binary', cellDates: true });
          const firstPage = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstPage];
          rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: false });
        }

        if (!rows.length) return;
        console.log('Raw data:', rows);
        const filteredRows = rows.filter((row, idx) => Array.isArray(row) && row.length > 0 && (idx === 0 || row.some((cell) => cell && cell.toString().trim() !== '')));
        if (filteredRows.length === 0) return;
        const header = filteredRows[0].map((h: string) => stripQuotes(h));
        const dataRows = filteredRows.slice(1);

        const recipients: Recipient[] = [];
        const donors: Donor[] = [];

        dataRows.forEach((row: any[]) => {
          if (!row.length) return;
          const item: any = {};
          header.forEach((key: string, idx: number) => {
            item[key] = typeof row[idx] === 'string' ? stripQuotes(row[idx]) : row[idx];
          });
          const type = (item['Type'] || '').toString().trim().toUpperCase();
          const fullPhenotype = this.sanitizeString(item['FullMatchPhenotype'] ?? '');
          const { firstName, lastName } = this.parseName(item['Name'] ?? '');

          if (type === 'R') {
            recipients.push(this.buildRecipient(item, firstName, lastName, fullPhenotype));
          } else if (type === 'D') {
            donors.push(this.buildDonor(item, firstName, lastName, fullPhenotype));
          }
        });

        this.excelDataRecipients = recipients;
        this.excelDataDonors = donors;

        if (recipients.length > 0 || donors.length > 0) {
          this.showSyncConfirmation(recipients, donors);
        } else {
          this.busy$.next(false);
          this.showNotification('No recipients or donors found in the file.');
        }
      });
    }
  }

  private parseName(rawName: string): { firstName: string; lastName: string } {
    let firstName = '';
    let lastName = '';
    if (rawName.includes(',')) {
      const parts = rawName.split(',');
      lastName = this.capitalizeName(this.sanitizeString(parts[0]));
      firstName = this.capitalizeName(this.sanitizeString(parts.slice(1).join(',').trim()));
    } else {
      const nameParts = rawName
        .trim()
        .split(/\s+/)
        .filter((part: string) => part.length > 0);
      if (nameParts.length === 1) {
        lastName = this.capitalizeName(this.sanitizeString(nameParts[0]));
      } else if (nameParts.length > 1) {
        firstName = this.capitalizeName(this.sanitizeString(nameParts[0]));
        lastName = this.capitalizeName(this.sanitizeString(nameParts.slice(1).join(' ')));
      }
    }
    return { firstName, lastName };
  }

  private parsePhenotype(fullPhenotype: string): { abPhenotype: string; drPhenotype: string } {
    if (!fullPhenotype) return { abPhenotype: '', drPhenotype: '' };
    const tokens = fullPhenotype.split(' ');
    const drIndex = tokens.findIndex((t) => t.startsWith('DR'));
    if (drIndex !== -1) {
      return { abPhenotype: tokens.slice(0, drIndex).join(' '), drPhenotype: tokens.slice(drIndex).join(' ') };
    }
    return { abPhenotype: fullPhenotype, drPhenotype: '' };
  }

  private buildRecipient(item: any, firstName: string, lastName: string, fullPhenotype: string): Recipient {
    const { abPhenotype, drPhenotype } = this.parsePhenotype(fullPhenotype);
    let virtualPRA: number | undefined;
    if (item['vPRA']) virtualPRA = Number(item['vPRA']);
    else if (item['CurrentPRA']) virtualPRA = Number(item['CurrentPRA']);
    return {
      enisNumber: this.sanitizeString(item['ETNR']),
      firstName,
      lastName,
      center: this.findCenter(this.sanitizeString(item['Center'])),
      dob: this.formatDate(this.parseDate(this.sanitizeString(item['DateOfBirth'] ?? '1900-01-01'))),
      gender: this.sanitizeString(item['Gender']),
      abo: this.sanitizeString(item['ABO']),
      abPhenotype,
      drPhenotype,
      urgency: this.sanitizeString(item['Urgency']),
      previousPRA:
        (item['Returned waiting days'] ?? item['Previous'])
          ? Number(item['Returned waiting days'] ?? item['Previous'])
          : undefined,
      virtualPRA,
      unacceptables: this.sanitizeString(item['UnacceptableAntigens']),
      dateScreening: this.formatOptionalDate(this.parseDate(this.sanitizeString(item['DateAbScreening']))),
      dateFirstDialysis: this.formatOptionalDate(this.parseDate(this.sanitizeString(item['FirstDialysis']))),
      primaryDiagnosis: this.sanitizeString(item['PrimaryDiagnosis']),
      postmortumwaitlist: this.isPostmortemWaitlist
    };
  }

  private buildDonor(item: any, firstName: string, lastName: string, fullPhenotype: string): Donor {
    return {
      dob: this.formatDate(this.parseDate(this.sanitizeString(item['DateOfBirth'] ?? '1900-01-01'))),
      lastName,
      firstName,
      gender: this.sanitizeString(item['Gender']),
      center: this.findCenter(this.sanitizeString(item['Center'])),
      abo: this.sanitizeString(item['ABO']),
      hla: fullPhenotype,
      enisNumber: this.sanitizeString(item['ETNR'])
    };
  }

  importRecipients(recipientList: Recipient[]): void {
    this.participantService.importRecipients(recipientList).subscribe({
      next: (response) => {
        this.responseSubjectNewRecipientList.next(response);
        recipientList.forEach((recipient) => {
          this.importLog.push({
            enisNumber: recipient.enisNumber || 'unknown',
            participantType: 'recipient',
            status: 'success',
            participantName: `${recipient.firstName || ''} ${recipient.lastName || ''}`.trim(),
            center: recipient.center?.name
          });
        });
        this.showNotification('Recipients imported: successfully!');
        console.log('Recipients imported:', response);
        this.sendImportLog();
        this.busy$.next(false);
        this.processed.emit(response.length);
      },
      error: (err) => {
        recipientList.forEach((recipient) => {
          this.importLog.push({
            enisNumber: recipient.enisNumber || 'unknown',
            participantType: 'recipient',
            status: 'failed',
            errorMessage: err.message || 'Import failed',
            participantName: `${recipient.firstName || ''} ${recipient.lastName || ''}`.trim(),
            center: recipient.center?.name
          });
        });
        this.showNotification('Something went wrong during the import.');
        console.log('Recipients not imported:', err);
        this.sendImportLog();
        this.busy$.next(false);
      }
    });
  }

  importDonors(donorList: Donor[]): void {
    this.participantService.importDonors(donorList).subscribe({
      next: (response) => {
        this.responseSubjectNewDonorList.next(response);
        donorList.forEach((donor) => {
          this.importLog.push({
            enisNumber: donor.enisNumber || 'unknown',
            participantType: 'donor',
            status: 'success',
            participantName: `${donor.firstName || ''} ${donor.lastName || ''}`.trim(),
            center: donor.center?.name
          });
        });
        this.showNotification('Donors imported: successfully!');
        console.log('Donors imported:', response);
        this.sendImportLog();
        this.busy$.next(false);
        this.processed.emit(response.length);
      },
      error: (err) => {
        donorList.forEach((donor) => {
          this.importLog.push({
            enisNumber: donor.enisNumber || 'unknown',
            participantType: 'donor',
            status: 'failed',
            errorMessage: err.message || 'Import failed',
            participantName: `${donor.firstName || ''} ${donor.lastName || ''}`.trim(),
            center: donor.center?.name
          });
        });
        this.showNotification('Something went wrong during the import.');
        console.log('Donors not imported:', err);
        this.sendImportLog();
        this.busy$.next(false);
      }
    });
  }

  sendImportLog(): void {
    if (this.importLog.length === 0) {
      console.log('No import log to send');
      return;
    }

    this.enisSyncService
      .logEnisSync({
        fileName: this.currentFileName,
        participants: this.importLog
      })
      .subscribe({
        next: (response) => {
          console.log('Import log saved successfully:', response);
        },
        error: (err) => {
          console.error('Failed to save import log:', err);
        }
      });
  }

  showSyncConfirmation(recipients: Recipient[], donors: Donor[]): void {
    const dialogRef = this.dialog.open(SyncConfirmationDialog, {
      width: '400px',
      data: {
        recipientCount: recipients.length,
        donorCount: donors.length,
        isPostmortemWaitlist: this.isPostmortemWaitlist
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        if (recipients.length > 0) {
          this.importRecipients(recipients);
        }
        if (donors.length > 0) {
          this.importDonors(donors);
        }
      } else {
        this.busy$.next(false);
        this.showNotification('Sync cancelled.');
      }
    });
  }

  //All names in the excel are full caps
  private capitalizeName(name: string): string {
    return name.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  }

  showNotification(message: string, action: string = 'Close'): void {
    this.snackBar.open(message, action, {
      duration: 3000,
      verticalPosition: 'top',
      horizontalPosition: 'center'
    });
  }

  private sanitizeString(str: string): string {
    const encoder = new TextEncoder();
    const decoder = new TextDecoder('utf-8');
    const encoded = encoder.encode(str);
    return decoder.decode(encoded).replace(/[^\x20-\x7E]/g, '');
  }

  private findCenter(name: string): Center {
    const sanitizedString = name.trim().toLowerCase();
    const matchingCenter = this.centers.find((center) => center.name.toLowerCase() === sanitizedString);
    if (!matchingCenter) {
      const unknownCenter = this.centers.find((center) => center.name === 'UNKNOWN');
      if (unknownCenter) {
        return unknownCenter;
      }
      return {
        id: '',
        name: 'UNKNOWN',
        oimName: 'unknown',
        fullName: 'Unknown',
        scoring: 0
      };
    }
    return matchingCenter;
  }

  private resolveShortYear(year: number): number {
    const currentYear = new Date().getFullYear();
    const yearWith1900 = year + 1900;
    const yearWith2000 = year + 2000;
    return currentYear - yearWith1900 > 100 && yearWith2000 <= currentYear ? yearWith2000 : yearWith1900;
  }

  private parseDashDate(sanitizedInput: string): Date | null {
    const parts = sanitizedInput.split('-');
    if (parts.length !== 3) return null;
    const isYearFirst = parts[0].length === 4;
    const day = Number.parseInt(isYearFirst ? parts[2] : parts[0], 10);
    const month = Number.parseInt(parts[1], 10) - 1;
    let year = Number.parseInt(isYearFirst ? parts[0] : parts[2], 10);
    if (year < 100) {
      year = this.resolveShortYear(year);
    }
    if (!Number.isNaN(day) && !Number.isNaN(month) && !Number.isNaN(year) && day >= 1 && day <= 31 && month >= 0 && month <= 11 && year >= 1900 && year <= new Date().getFullYear()) {
      return new Date(year, month, day);
    }
    return null;
  }

  private parseDate(input: string): Date | null {
    const sanitizedInput = this.sanitizeString(input);
    if (sanitizedInput.includes('-')) {
      const result = this.parseDashDate(sanitizedInput);
      if (result !== null) return result;
    }
    const parsedDate = new Date(sanitizedInput);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  private formatDate(date: Date | null | undefined): string {
    if (!date) return '1900-01-01';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private formatOptionalDate(date: Date | null | undefined): string | undefined {
    if (!date) return undefined;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const formatted = `${year}-${month}-${day}`;
    return formatted === '1900-01-01' ? undefined : formatted;
  }

  private getAllCenters(): void {
    this.centerService.getAllCenters().subscribe({
      next: (data) => {
        this.centers = data;
      },
      error: (error) => {
        console.log(error);
      }
    });
  }

  selectText() {
    const elem: HTMLInputElement = this.input.nativeElement;
    elem && typeof elem.select === 'function' && elem.select();
  }

  copied() {
    this.notify.withAutodismiss('Parameters copied to clipboard');
  }
}

// Confirmation Dialog Component
@Component({
  selector: 'sync-confirmation-dialog',
  template: `
    <h2 mat-dialog-title>Confirm Sync</h2>
    <mat-dialog-content>
      <p>Are you sure you want to sync the following participants?</p>
      <ul style="list-style: none; padding-left: 0;">
        <li *ngIf="data.donorCount > 0">
          <strong>{{ data.donorCount }}</strong> donor{{ data.donorCount > 1 ? 's' : '' }}
        </li>
        <li *ngIf="data.recipientCount > 0">
          <strong>{{ data.recipientCount }}</strong> recipient{{ data.recipientCount > 1 ? 's' : '' }}
        </li>
      </ul>
      <div *ngIf="data.isPostmortemWaitlist" style="margin-top: 16px; padding: 12px; background-color: #fff3e0; border-left: 4px solid #ff9800; border-radius: 4px;">
        <strong>Note:</strong> Participants will be imported as <strong>postmortem waitlist patients</strong>
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button [mat-dialog-close]="false">Cancel</button>
      <button mat-raised-button color="primary" [mat-dialog-close]="true">Confirm</button>
    </mat-dialog-actions>
  `,
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule]
})
export class SyncConfirmationDialog {
  constructor(@Inject(MAT_DIALOG_DATA) public data: { recipientCount: number; donorCount: number; isPostmortemWaitlist: boolean }) {}
}
